<template>
  <form @submit.prevent="handleSubmit">
    <t-input
      v-model="paymentForm.cardHolderName"
      :label="$t('credit_card.name') + '*'"
      class="w-full mt-4"
      :error="$v.paymentForm.cardHolderName.$dirty && $v.paymentForm.cardHolderName.$error"
    >
      <template v-if="!$v.paymentForm.cardHolderName.valid" #error>
        <template v-if="cardError === 'name'">{{ $t('credit_card.card_holder_confirm') }}</template>
        <template v-else-if="cardError === 'name_format'">{{
          $t('credit_card.card_holder_format_confirm')
        }}</template>
      </template>
    </t-input>

    <t-input
      v-model="paymentForm.cardNumber"
      :formatter="formatCardNum"
      class="w-full mt-4"
      :label="$t('credit_card.number') + '*'"
      :error="$v.paymentForm.cardNumber.$dirty && $v.paymentForm.cardNumber.$error"
    >
      <template v-if="!$v.paymentForm.cardNumber.valid" #error>
        {{ $t('credit_card.card_number_confirm') }}
      </template>
    </t-input>

    <div class="mt-4">
      <div>{{ $t('credit_card.expiration') }}*</div>
      <div class="flex w-full mt-1">
        <t-select
          v-model="paymentForm.month"
          :options="monthList"
          :placeholder="$t('month')"
          class="w-1/2"
          :error="$v.paymentForm.month.$dirty && $v.paymentForm.month.$error"
        >
        </t-select>
        <t-select
          v-model="paymentForm.year"
          :options="yearList"
          :placeholder="$t('year')"
          class="w-1/2 ml-2"
          :error="$v.paymentForm.year.$dirty && $v.paymentForm.year.$error"
        ></t-select>
      </div>
      <span
        v-if="!$v.paymentForm.year.valid || !$v.paymentForm.month.valid"
        class="t-input-error-message"
        >{{ $t('credit_card.expiration_confirm') }}</span
      >
    </div>

    <div class="mt-4">
      <div class="flex w-full mt-1">
        <t-input
          v-model="paymentForm.cvv"
          label="CVV*"
          class="w-1/2"
          :formatter="formatCVC"
          :error="$v.paymentForm.cvv.$dirty && $v.paymentForm.cvv.$error"
        ></t-input>
        <div class="w-1/2 ml-2"></div>
      </div>
      <span
        v-if="$v.paymentForm.cvv.$dirty && $v.paymentForm.cvv.$error"
        class="t-input-error-message"
        >{{ $t('credit_card.card_cvv_confirm') }}</span
      >
    </div>

    <theme-button
      :loading="loading"
      :disabled="disabled"
      type="submit"
      class="w-full mt-4 px-3 py-2"
      >{{ $t('base.pay') }}</theme-button
    >
  </form>
</template>

<script>
import TInput from 'ChatWidget/Common/TInput'
import { email, required, minLength } from 'vuelidate/lib/validators'
import formatCardNum, { formatCVC } from 'Utilities/normalizeCard'
import { registerCard, loadScript } from 'Utilities/thirdPartyPayment'
import { genMonthList, genYearList } from 'Utilities/TimerHelper'
import creditCardType from 'credit-card-type'
import ThemeButton from 'javascript/Common/ThemeButton'
import TSelect from 'ChatWidget/Common/TSelect'
import { mapState } from 'pinia'
import { useSettingsStore } from 'Store/settings'

export default {
  name: 'CreditCardForm',
  components: { TSelect, ThemeButton, TInput },

  props: {
    loading: Boolean,
    disabled: Boolean
  },

  data() {
    return {
      isSubmitting: false,
      cardError: null,
      paymentForm: {
        email: null,
        cardHolderName: null,
        cardNumber: null,
        month: null,
        year: null,
        cvv: null
      }
    }
  },

  computed: {
    ...mapState(useSettingsStore, {
      paymentGatewaySetting: 'paymentGatewaySetting'
    }),

    monthList() {
      return genMonthList().map((month) => ({ value: month, text: month }))
    },

    yearList() {
      return genYearList().map((year) => ({ value: year, text: year }))
    },

    cardType() {
      const ambiguousCards = creditCardType(this.paymentForm.cardNumber)
      return ambiguousCards.length > 0 ? ambiguousCards[0].type : undefined
    }
  },

  created() {
    loadScript({
      type: this.paymentGatewaySetting.name,
      url: this.paymentGatewaySetting.script
    })
  },

  methods: {
    formatCardNum,

    formatCVC(value) {
      return formatCVC(value, this.cardType)
    },

    registerCard() {
      return new Promise((resolve, reject) => {
        registerCard(
          {
            shopId: this.paymentGatewaySetting?.shop_id,
            token: this.paymentGatewaySetting?.token,
            type: this.paymentGatewaySetting?.name,
            holdername: this.paymentForm.cardHolderName,
            cardno: (this.paymentForm.cardNumber || '').replace(/\s/g, ''),
            expireMonth: this.paymentForm.month,
            expireYear: this.paymentForm.year,
            cvv: this.paymentForm.cvv
          },
          (registerCardResult) => {
            const resultDetails = registerCardResult?.resultDetails
            if (resultDetails === 'success') {
              resolve(registerCardResult)
            } else {
              reject(registerCardResult)
            }
          }
        )
      })
    },

    handleSubmit() {
      this.cardError = null
      this.$v.$touch()
      if (this.$v.$invalid) return

      this.registerCard()
        .then((result) => {
          this.$emit('submit', { card_token: result.cardToken })
        })
        .catch((err) => {
          this.cardError = err?.resultDetails || 'unknown'
        })
    }
  },

  validations: {
    paymentForm: {
      email: {
        valid(value) {
          return value ? email(value) : true
        }
      },
      cardHolderName: {
        required,
        valid() {
          return !['name', 'name_format'].includes(this.cardError)
        }
      },
      cardNumber: {
        required,
        minLength: minLength(14),
        valid() {
          return this.cardError !== 'card'
        }
      },
      month: {
        required,
        valid() {
          return this.cardError !== 'date'
        }
      },
      year: {
        required,
        valid() {
          return this.cardError !== 'date'
        }
      },
      cvv: {
        required,
        maxLength(value) {
          const length = this.cardType === 'american-express' ? 4 : 3
          return value?.length === length
        },
        valid() {
          return this.cardError !== 'cvv'
        }
      }
    }
  }
}
</script>
